<template lang="pug">
// Компонент обернут в LazyHydrate на уровне шаблона, потому rendered меняется только при его
// видимости
ErrorBoundary(moduleName="CommentsContainer")
  template(#error)
    p Неизвестная ошибка
  .comments-wrapper(v-if="!isServiceError")
    a.comments__anchor(ref='commentsAnchor' href='#comments')
    a.comments__anchor(ref='commentsAnchorInput' href='#write-a-comment')
    #comments.comments__anchor-target(ref="commentsAnchorTarget" )
    #write-a-comment.comments__anchor-target(ref="commentsInputAnchorTarget")
    .comments__container
      .comments__container-inner
        TalkerRoot(
          ref="talker"
          :id="id"
          @show-login="showLoginPopup"
          @analytics="sendAnalytics"
        )
</template>

<script lang="ts">
  import type { PropType } from 'vue';
  import { isIOS } from 'mobile-device-detect';
  import {
    TALKER_API_URL,
    PUSHER_API_URL,
    // ACCESS_TOKEN_COOKIE_NAME,
    isProduction,
  } from '~/constants/config';

  import getSlugFromUrl from '~/utils/getSlugFromUrl';
  import { useIndexStore } from '~/store';
  import { useAuthRedirect } from '@devhacker/shared/features/auth/utils/authRedirect';
  import { useRecipesListWidgetStore } from '~/store/recipesListWidget';
  import { usePageStore } from '~/store/pagesData';
  import { useServiceErrorsStore } from '~/store/errors';
  import { useTalkerStore } from '@devhacker/shared/plugins/talker/store/talker';
  import TalkerRoot from '@devhacker/shared/plugins/talker/components/TalkerRoot.vue';
  import ErrorBoundary from '@devhacker/shared/components/ErrorBoundary.vue';
  import { useModal } from '@devhacker/shared/plugins/modal/useModal';
  import { useSystemPush } from '@devhacker/shared/features/system-push/useSystemPush';

  export default defineNuxtComponent({
    name: 'CommentsContainer',

    TALKER_API_URL,
    PUSHER_API_URL,

    components: {
      TalkerRoot,
      ErrorBoundary,
    },
    // ACCESS_TOKEN_COOKIE_NAME,

    props: {
      id: {
        type: Number as PropType<number>,
        required: true,
      },
    },

    setup() {
      return {
        indexStore: useIndexStore(),
        modal: useModal(),
        pageStore: usePageStore(),
        recipesListWidgetStore: useRecipesListWidgetStore(),
        route: useRoute(),
        serviceErrorsStore: useServiceErrorsStore(),
        talkerStore: useTalkerStore(),
      };
    },

    data() {
      return {
        timer: null as NodeJS.Timeout | null,
        observer: null as MutationObserver | null,
        isFirstOpenModal: true,
      };
    },

    computed: {
      articleCommentsError() {
        return this.serviceErrorsStore.articleCommentsError;
      },

      recipeCommentsError() {
        return this.serviceErrorsStore.recipeCommentsError;
      },

      currentArticle() {
        return this.pageStore.currentArticle;
      },

      isServiceError() {
        return this.articleCommentsError || this.recipeCommentsError;
      },

      hash(): string {
        return this.route.hash;
      },

      currentSlug(): string {
        return getSlugFromUrl(this.route.fullPath);
      },

      postId() {
        return this.currentArticle?.id ?? this.recipesListWidgetStore.currentRecipeId;
      },
    },

    async beforeMount() {
      if (this.isServiceError || !this.postId) {
        return;
      }

      this.talkerStore.setPostId(this.postId);
    },

    async mounted() {
      if (this.isServiceError) return;

      this.createAuthEventBus();
      this.goToHash();

      await this.$nextTick();

      try {
        if (!this.postId) throw new Error('No postId provided');

        // раньше socket.io-client находился в shared и подключался там
        // но из-за проблем со сборкой на избе, его пришлось вынести сюда

        // @ts-expect-error Socket.io is attached for TalkerRoot legacy API
        window.io = (await import('socket.io-client')).default;

        // @ts-expect-error TalkerRoot exposes createEcho through component ref
        this.$refs.talker.createEcho(this.postId);
      } catch (e) {
        console.error('Error while mounting comments', e);
      }
    },

    unmounted() {
      window.removeEventListener('success-refresh-comments', this.successRefreshListener);
      window.removeEventListener('failed-refresh-comments', this.failedRefreshListener);
    },

    methods: {
      sendAnalytics(analytics) {
        this.$sendYandexMetrika({
          level1: `${analytics.eventName}_${analytics.action}`,
        });
      },

      createAuthEventBus() {
        document.addEventListener('success-refresh-comments', this.successRefreshListener);
        document.addEventListener('failed-refresh-comments', this.failedRefreshListener);
      },

      prepareAnalyticsData(
        analyticsData: Record<string, unknown>,
      ): Record<string, unknown> {
        const {
          eventName,
          action,
          attachments,
          currency,
          element,
          value,
          slugReferer,
          errorMessage,
          item,
        } = analyticsData;

        return {
          event: eventName,
          error_message: errorMessage,
          slug_location: this.currentSlug,
          slug_referer: slugReferer,
          action,
          attachments,
          currency,
          element,
          value,
          item,
        };
      },

      checkIOS(): boolean {
        return isIOS;
      },

      createObserver(callback: MutationCallback) {
        this.observer = new MutationObserver(callback);
        this.observer.observe(document, {
          childList: true,
          subtree: true,
        });
      },

      goToHash() {
        if (this.hash === '#write-a-comment') {
          this.scrollToInput();

          this.createObserver(this.scrollToInputCallback);
        }

        if (this.hash === '#comments') {
          this.scrollToComments();

          this.createObserver(this.scrollToCommentsCallback);
        }

        if (this.hash?.match(/comment-[0-9]*/gm)) {
          this.scrollToComment(this.hash);

          this.createObserver(this.scrollToCommentCallback);
        }
      },

      scrollToComments() {
        // @ts-expect-error Template ref is not typed in options component
        this.$refs.commentsAnchorTarget.scrollIntoView();
      },

      scrollToComment(commentId: string) {
        const comment = document.querySelector(commentId);
        comment?.scrollIntoView();
      },

      scrollToCommentCallback() {
        if (this.timer) clearTimeout(this.timer);
        this.scrollToComment(this.hash);
        this.createDestroyObserverTimer();
      },

      scrollToCommentsCallback() {
        if (this.timer) clearTimeout(this.timer);
        this.scrollToComments();
        this.createDestroyObserverTimer();
      },

      createDestroyObserverTimer() {
        this.timer = setTimeout(() => {
          this.observer?.disconnect();
        }, 1000);
      },

      scrollToInputCallback(): void {
        if (this.timer) clearTimeout(this.timer);
        this.createDestroyObserverTimer();
        this.scrollToInput();
      },

      scrollToInput() {
        // @ts-expect-error Template ref is not typed in options component
        this.$refs.commentsInputAnchorTarget.scrollIntoView();
        setTimeout(() => {
          // @ts-expect-error TalkerRoot exposes focusMainInput through component ref
          this.$refs.talker.focusMainInput();
        }, 150);
      },

      prepareAnalyticModal(e: Record<string, unknown>) {
        const { eventName, action, attachments, currency, element, value, slugReferer, item } = e;

        const preparedData = {
          event: eventName,
          slug_location: this.currentSlug,
          slug_referer: slugReferer,
          action,
          attachments,
          currency,
          element,
          value,
          item,
        };

        return preparedData;
      },

      // Действие гостя (отправка, реакция, жалоба) — мягкий пуш вместо редиректа
      showLoginPopup(_e: { callback?: () => void }): void {
        useSystemPush().requestAuthPush();
      },

      successRefreshListener() {
        console.log('refresh-success-talker-frontend');
      },

      failedRefreshListener(event: Event) {
        const detail = (event as CustomEvent).detail;
        const isBanned =
          detail?.response?.data?.data?.isBanned || detail?.request?.data?.data?.isBanned;
        if (!isBanned) {
          this.indexStore.clearSessionLocally();
          if (!isProduction) {
            console.error('refresh failed CommentsContainer', detail);
          }
          // При ошибке refresh — redirect на auth-sso вместо модалки
          if (this.isFirstOpenModal) {
            useAuthRedirect().login();
            this.isFirstOpenModal = false;
          }
        }
      },
    },
  });
</script>

<style lang="scss">
  $borderColor: #e7e7e7;

  .comments-wrapper {
    position: relative;

    // снижает количество элементов для первоначальной отрисовки
    contain-intrinsic-width: auto 100vw;
    contain-intrinsic-height: auto 500px;

    @include tablet {
      contain-intrinsic-width: auto 780px;
      contain-intrinsic-height: auto 525px;
    }
  }

  .comments__anchor-target {
    display: block;
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 48px;
    transform: translateY(-100%);
    pointer-events: none;
  }

  .comments__container {
    border-top: 3px solid var(--main-color);
    max-width: 100vw;
    min-height: 457px;
    display: flex;
    margin-bottom: 24px;
    @include tablet() {
      max-width: 640px;
      min-height: 497px;
      margin-left: auto;
      margin-right: auto;
    }

    @include big-tablet() {
      max-width: 780px;
      @include one-column() {
        max-width: 970px;
      }

      @include broadside {
        max-width: 780px;
      }
    }
  }

  .comments__container-inner {
    position: relative;
    padding: 24px 16px 16px;
    background: white;
    width: 100%;
    scroll-margin-top: 60px;
    // for safari
    scroll-snap-margin-top: 60px;

    @include tablet() {
      padding: 24px;
    }

    // маразм с бордерами - высота бордеров не учитывается в фигме при расчете паддингов, поэтому приходится делать так
    // иначе ставят баги на то, что элемент не соответствует макету на сумму бордеров - я ЗАЕБАЛСЯ объяснять эту фишку, везде бордеры теперь делаю псевдоэлементами
    // симуляция при помощи внутренней тени работает не всегда хорошо - начинаются проблемы с z-index
    &::after {
      position: absolute;
      content: '';
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
      border-bottom: 1px solid $borderColor;
      pointer-events: none;

      @include tablet() {
        border-left: 1px solid $borderColor;
        border-right: 1px solid $borderColor;
      }
    }
  }
</style>
